import store from "@/store";
import axios from "@/plugins/axios";
import {planningService} from "@/app/services/planning.service";
import {userService} from "@/services/user.service";
import {userServiceV2} from "@/app/services/user.service";
import {configService} from "@/services/config.service";
import {resolveEngine} from "@/app/helpers/resolve.engine";
import {ContextAware} from "@/app/contexts/detail.context";

export type PartyRoleType = | "USER" | "USER_IN_CURRENT_ORGANISATION" | "ORGANISATION" | "BCCA" | "ORGANISATIONS_WITH_ROLE" | "USERS_WITH_ROLE" | "ALL" | "ALL_USERS" ;

export interface Party {
    id: string;
    contactId: string;
    roleId: string
    partyRoleId: string;
    portalId: string; // free reference, used by exchange for party nameing
    contactType: PartyRoleType;
}

class PartyService {


    async addPartiesWithMetadataResolver(involvedParties:{
        portalId : string //reference to identify the reason the user was added as party
        roleId? : string //role of user/org to grant to
        contactId? : string //id of user/org to grant to
        contactType : ("USER_IN_CURRENT_ORGANISATION" |    "USER" |    "ORGANISATION" |    "ALL_USERS" |    "BCCA" |    "USERS_WITH_ROLE" |    "ORGANISATIONS_WITH_ROLE"),
        partyRoleId?: string | null | undefined
    }[] | undefined, context: ContextAware | undefined | null, entityId: string, entityType: string) {
        if(!involvedParties || !Array.isArray(involvedParties) || involvedParties.length <= 0) return;
        if(!entityId) return;

        if(context){
            involvedParties = context?.resolvePlaceholders(involvedParties) ;
            involvedParties = involvedParties || [];
            if(!Array.isArray(involvedParties)) return;
        }

        for (const involvedParty of involvedParties) {
            if(!involvedParty.contactType) continue;

            if(context){
                if(involvedParty && involvedParty.contactId && involvedParty.contactId.startsWith("data.")){
                    involvedParty.contactId = resolveEngine.resolveFieldValue(involvedParty.contactId, context);
                }
            }
            await partyService.addParty(entityType, entityId, involvedParty.contactType, involvedParty.contactId || involvedParty.roleId || null, involvedParty.partyRoleId)
        }
    }

    async addParties(involvedParties:{
        portalId : string //reference to identify the reason the user was added as party
        roleId? : string //role of user/org to grant to
        contactId? : string //id of user/org to grant to
        contactType : ("USER_IN_CURRENT_ORGANISATION" |    "USER" |    "ORGANISATION" |    "ALL_USERS" |    "BCCA" |    "USERS_WITH_ROLE" |    "ORGANISATIONS_WITH_ROLE"),
        partyRoleId?: string | null | undefined
    }[] | undefined, entityId: string, entityType: string) {
        if(!involvedParties || !Array.isArray(involvedParties) || involvedParties.length <= 0) return;
        if(!entityId) return;

        for (const involvedParty of involvedParties) {
            if(!involvedParty.contactType) continue;
            await partyService.addParty(entityType, entityId, involvedParty.contactType, involvedParty.contactId || involvedParty.roleId || null, involvedParty.partyRoleId)
        }
    }

    async addUser(entityType: string, entityId: string | null, userId: string | null, userRoleId?: string, contextField?: string) {
        return this.addParty(entityType,entityId, "USER", userId, userRoleId, contextField);
    }

    async addOrg(entityType: string, entityId: string | null, orgId: string | null, orgRoleId?: string, contextField?: string) {
        return this.addParty(entityType,entityId, "ORGANISATION", orgId, orgRoleId, contextField);
    }

    async addParty(entityType: string, entityId: string | null, contactType: PartyRoleType, contactId: string | null, contactRoleId?: string | null, contextField?: string): Promise<string>{
        contactType = contactType.toUpperCase().trim().replaceAll(' ', '_') as PartyRoleType;
        if(entityType === null || entityId === null || entityId === '$nan' || entityId.startsWith("data.")){
            return Promise.reject(()=> "null entity type or id");
        }
        const url = this.getAddPartyUrl(entityType, entityId);
        if(url == null) return Promise.reject(()=> "Invalid url assembled to add party");

        if(contactType === "USER_IN_CURRENT_ORGANISATION" && contactId !== null){
            const userInfos = await userServiceV2.getUsersByIds([contactId]);
            if(userInfos.length <= 0){
                return Promise.reject(()=> "Invalid user id");
            }
            //check for in company not done for now.
            contactType = "USER";
        }

        if(contactType === "ALL_USERS"){
            contactType = "ALL";
        }

        if(contactType === "BCCA"){
            contactId = store.state.appConfig.bccaId;
            contactType = "ORGANISATION";
        }

        if(contactType !== "ALL" && (contactId === null || contactId === '$nan' || entityId.startsWith("data."))){
            //warning message, but we don't want to blokc progress
            console.warn("unable to add party, contact did not resolve", entityType, entityId, contactType, contactId, contactRoleId, contextField)
            return Promise.resolve("nan");
        }

        return axios
            .post(url, {
                contactType: contactType,
                contactId: contactType === "USER" || contactType === "ORGANISATION" ? contactId : undefined,
                roleId: contactType === "USERS_WITH_ROLE" || contactType === "ORGANISATIONS_WITH_ROLE" ? contactId : undefined,
                partyRoleId: contactRoleId ? contactRoleId : undefined,
                portalId: contextField ? contextField : undefined
            })
            .then((response) => {
                return response.data;
            });
    }

    async listParties(entityType: string, entityId: string): Promise<Party[]>{
        if(entityType == null || entityId == null)
            return Promise.reject(()=> "null entity type or id");
        const url = this.getListPartyUrl(entityType, entityId);
        if(url == null) return Promise.reject(()=> "Invalid url assembled to list party");

        return axios
            .get(url)
            .then((response) => {
                return response.data;
            });
    }

    async deleteParty(entityType: string, entityId: string, partyId: string): Promise<any>{
        if(entityType == null || entityId == null)
            return Promise.reject(()=> "null entity type or id");
        const url = this.deletePartyUrl(entityType, entityId, partyId);
        if(url == null) return Promise.reject(()=> "Invalid url assembled to delete party");

        return axios
            .delete(url)
            .then((response) => {
                return response.data;
            });
    }

    private getAddPartyUrl(entityType: string, entityId: string): string | null {
        switch (entityType.toLowerCase().trim()) {
            case "ae_dossier":
            case "dossier":
                return `${store.state.appConfig.apiBaseUrl}api/v2/dossiers/${entityId}/addParty`
            case "ae_product":
            case "product":
                return `${store.state.appConfig.apiBaseUrl}api/v2/products/${entityId}/party`
            case "nrq_checkin":
            case "checkin":
                return `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${entityId}/party`
            case "nrq_planning":
            case "planning":
                return `${store.state.appConfig.apiBaseUrl}api/v2/planning/${entityId}/party`
            case "nrq_schedule":
            case "ae_document":
            case "document":
                return `${store.state.appConfig.apiBaseUrl}api/v2/documents/${entityId}/party`
            case "nrq_exchange":
            case "exchange":
                return null;
            default:
                return null;
        }
    }

    private getListPartyUrl(entityType: string, entityId: string): string | null {
        switch (entityType.toLowerCase().trim()) {
            case "ae_dossier":
            case "dossier":
                return `${store.state.appConfig.apiBaseUrl}api/v2/dossiers/${entityId}/party`
            case "ae_product":
            case "product":
                return `${store.state.appConfig.apiBaseUrl}api/v2/products/${entityId}/party`
            case "nrq_checkin":
            case "checkin":
                return `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${entityId}/party`
            case "nrq_planning":
            case "planning":
                return `${store.state.appConfig.apiBaseUrl}api/v2/planning/${entityId}/party`
            case "nrq_schedule":
            case "ae_document":
            case "document":
                return `${store.state.appConfig.apiBaseUrl}api/v2/documents/${entityId}/party`
            case "nrq_exchange":
            case "exchange":
                return null;
            default:
                return null;
        }
    }

    private deletePartyUrl(entityType: string, entityId: string, partyId: string): string | null {
        switch (entityType.toLowerCase().trim()) {
            case "ae_dossier":
            case "dossier":
                return `${store.state.appConfig.apiBaseUrl}api/v2/dossiers/${entityId}/removeparty/${partyId}`
            case "ae_product":
            case "product":
                return `${store.state.appConfig.apiBaseUrl}api/v2/products/${entityId}/party${partyId}`
            case "nrq_checkin":
            case "checkin":
                return `${store.state.appConfig.apiBaseUrl}api/v2/checkin/${entityId}/party/${partyId}`
            case "nrq_planning":
            case "planning":
                return `${store.state.appConfig.apiBaseUrl}api/v2/planning/${entityId}/party/${partyId}`
            case "nrq_schedule":
            case "ae_document":
            case "document":
                return `${store.state.appConfig.apiBaseUrl}api/v2/documents/${entityId}/party/${partyId}`
            case "nrq_exchange":
            case "exchange":
                return null;
            default:
                return null;
        }
    }
}
export const partyService = new PartyService();
